import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ Icons */
import { AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components - Premium Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "20px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
    height: "100%", // Ensures uniform card height in the grid
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "15px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.85rem",
    lineHeight: 1.5,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
    margin: 0,
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/**
 * TypeScriptCheatSheet Component
 * * A consolidated reference module for TypeScript syntax.
 * Features: Grid layout for scannability, TTS for auditory learners,
 * and clipboard tools for quick code reuse.
 */
export default function TypeScriptCheatSheet() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ UI State Management */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ FAB Visibility Logic */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    /* ✅ Audio Controls */
    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) {
            setIsSpeaking(false);
            return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
    };

    /* ✅ Utility: Copy Code */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ✅ Cheat Sheet Data */
    const cheatSheetData = useMemo(() => [
        {
            title: "Basic Types",
            desc: "Numbers, Strings, Booleans, Arrays, and Tuples.",
            code: `let age: number;\nlet name: string;\nlet isOnline: boolean;\nlet ids: number[] = [1, 2];\nlet pair: [string, number];`
        },
        {
            title: "Special Types",
            desc: "Handling any, unknown, void, and never.",
            code: `let value: any;\nlet input: unknown;\nfunction warn(): void {}\nfunction crash(): never {\n  throw new Error();\n}`
        },
        {
            title: "Aliases & Unions",
            desc: "Custom type names and multi-type logic.",
            code: `type Score = number | "N/A";\ntype Person = {\n  name: string\n} & { age: number };`
        },
        {
            title: "Functions",
            desc: "Parameter typing and return values.",
            code: `function add(\n  a: number, \n  b: number = 0\n): number {\n  return a + b;\n}`
        },
        {
            title: "Generics",
            desc: "Reusable, type-safe placeholders.",
            code: `function identity<T>(arg: T): T {\n  return arg;\n}\n\nlet n = identity<number>(5);`
        },
        {
            title: "Utility Types",
            desc: "Built-in type transformation tools.",
            code: `Partial<T> // Optional\nReadonly<T> // Immutable\nPick<T, K> // Select\nOmit<T, K> // Remove`
        }
    ], []);

    return (
        <PageWrapper>
            {/* 🏆 Header Section */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h4" : "h3"}
                    sx={{
                        fontWeight: 800,
                        backgroundColor: "goldenrod",
                        color: "#100720",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    <RocketLaunchIcon fontSize="large" /> CHEAT SHEET
                </Typography>
            </Box>

            <Grid container spacing={3} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Summary Introduction */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Quick Reference Guide
                            </Typography>
                            <IconButton onClick={() => handleSpeak("This cheat sheet provides a quick look at TypeScript fundamentals, including types, functions, and generics.")} sx={{ color: "goldenrod" }}>
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Use this guide to quickly recall TypeScript syntax. It covers everything from basic primitive annotations to advanced utility types and generics.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* ✅ Dynamic Cheat Sheet Cards */}
                {cheatSheetData.map((item, i) => (
                    <Grid item xs={12} md={6} lg={4} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={20} />
                                    </Avatar>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Listen">
                                        <IconButton size="small" onClick={() => handleSpeak(`${item.title}: ${item.desc}`)} sx={{ color: "goldenrod" }}>
                                            <VolumeUpIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy"}>
                                        <IconButton size="small" onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                            <Typography variant="caption" display="block" sx={{ mb: 2, opacity: 0.7, minHeight: "32px" }}>
                                {item.desc}
                            </Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Navigation Footer */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/typescript-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO SUMMARY
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Control FABs */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={stopSpeech}>
                            <StopIcon />
                        </Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab
                        size="small"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        sx={{ bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                    >
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
